import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
  AgreementSupportTerms,
  AgreementSupportTermsFormGroup,
} from 'src/app/pages/agreements/types/agreement-create-update-form.model';
import { UtilityService } from 'src/app/shared/services/utility.service';

@Component({
  selector: 'app-agreement-support-terms-form',
  templateUrl: './agreement-support-terms-form.component.html',
  styleUrls: ['./agreement-support-terms-form.component.scss'],
})
export class AgreementSupportTermsFormComponent implements OnInit {
  /** Boolean indicating wether form should be pre-filled or not */
  @Input() isPreFilled = false;

  /** Set the agreement support term form values from the terms input */
  @Input() set terms(terms: AgreementSupportTerms) {
    this.supportTermsForm.patchValue(terms);
  }

  /** The support terms form change event emitter */
  @Output() valueChange = new EventEmitter<Partial<AgreementSupportTerms>>();

  /** Form group for agreement support terms */
  supportTermsForm = new FormGroup<AgreementSupportTermsFormGroup>({
    supportCancel: new FormControl<string>(''),
    supportChange: new FormControl<string>(''),
    supportMinHours: new FormControl<string>(''),
    supportFeedback: new FormControl<string>(''),
  });

  /** Data used to set the display for each of the terms */
  supportTermsDisplayData: { control: string; label: string; description: string; examples: string[] }[] = [];

  constructor(private utilityService: UtilityService) {}

  ngOnInit(): void {
    this.setSupportTermsDisplay();
  }

  /**
   * Emit the support terms changes.
   */
  onSupportTermsChange(): void {
    this.valueChange.emit(this.supportTermsForm.value);
  }

  /**
   * Open NDIS price guide
   */
  protected priceGuide(): void {
    this.utilityService.openExternalLink('https://www.ndis.gov.au/providers/pricing-arrangements');
  }

  /**
   * Sets the label, description and examples of all the terms to be displayed.
   */
  private setSupportTermsDisplay(): void {
    for (const [supportTerm] of Object.entries(this.supportTermsForm.controls)) {
      this.supportTermsDisplayData.push({
        control: supportTerm,
        label: this.getSupportTermLabel(supportTerm),
        description: this.getSupportTermDescription(supportTerm),
        examples: this.getSupportTermExamples(supportTerm),
      });
    }
  }

  /**
   * Given a support term control name, return the label of the term to be displayed on the template.
   *
   * @param term - the control name of the term to be displayed.
   * @returns the label of the term to be displayed.
   */
  private getSupportTermLabel(term: string): string {
    switch (term) {
      case 'supportCancel':
        return 'Cancelling sessions (optional)';
      case 'supportChange':
        return 'Changing or adding sessions (optional)';
      case 'supportMinHours':
        return 'Finishing early and overtime (optional)';
      case 'supportFeedback':
        return 'Feedback, complaints or disputes (optional)';
      default:
        return '';
    }
  }

  /**
   * Given a support term control name, return the description of the term to be displayed on the template.
   *
   * @param term - the control name of the term to be displayed.
   * @returns the description of the term to be displayed.
   */
  private getSupportTermDescription(term: string): string {
    switch (term) {
      case 'supportCancel':
        return 'Set expectations about cancellations by you and your client by including minimum notice periods, how to cancel a session and if and when a charge may apply.';
      case 'supportChange':
        return 'State the notice you need if your client would like to add or change sessions and how to contact you.';
      case 'supportMinHours':
        return "State your minimum hours per session. For finishing early, state if you'll charge a full or partial hourly rate. For overtime, state how you'll charge.";
      case 'supportFeedback':
        return "State how you'd like your client to communicate feedback or suggestions on your work. Include the process for handing a complaint or dispute.";
      default:
        return '';
    }
  }

  /**
   * Given a support term control name, return the examples of the term to be displayed on the template.
   *
   * @param term - the control name of the term to be displayed.
   * @returns the examples of the term to be displayed.
   */
  private getSupportTermExamples(term: string): string[] {
    switch (term) {
      case 'supportCancel':
        return [
          `If you need to cancel a session, contact me by messaging me on (phone number/Mable chat) as soon as possible.
           If you provide less than 24 hours' notice, support hours will be submitted for the full cost of the cancelled session.
           <br><br>If I need to cancel a session due to personal illness or other unexpected circumstances, I'll contact you
           by messaging you on (phone number/Mable chat) as soon as possible. I will give you 2 weeks' notice of any planned
          leave. It is your responsibility to find alternative support options if I am unavailable to attend a support session.`,
          "If for any reason you wish to cancel your scheduled support session, you are advised to give me at least 48 hours' notice. Any cancellations made within 48 hours of the scheduled support will incur the full session fee.",
        ];
      case 'supportChange':
        return [
          "Please give 3 to 5 days' notice before the start of the session if there are changes to date, time or service type.",
          "Any extension of an agreed session length must be verbally agreed on by both (client name) and me. If sessions need to be changed on an ongoing basis, the agreement can be amended to reflect the change in support need - minimum 2 weeks' notice.",
        ];
      case 'supportMinHours':
        return [
          "My minimum session hours are 2 hours on weekdays and 1 hour on weekends. If a session finishes early, I'll charge my full rate for the session booked. If a session finishes late, I'll charge the extra time at my hourly rate.",
          'Minimum session is 1 hour and time thereafter will be charged in 15-minute increments. If you book a session, eg, 9 am to 12 pm, which is reflected in the agreement, and the session ends early, the whole session will be charged for. I can accommodate overtime where my availability allows and it will be charged at the appropriate day and time rate.',
        ];
      case 'supportFeedback':
        return [
          "If there's an issue with, or you have feedback on my support, please contact me on Mable chat so we can discuss and come to a resolution. If there is a serious issue, please report it to Mable's Trust and Safety team. I'm required to report incidents to Mable within 24 hours which I will do via the incidents reporting form when I submit my support hours.",
          'If you would like to provide feedback on any support received, please email me at (email address). You can also raise a complaint with Mable by emailing info@mable.com.au.',
        ];
      default:
        return [];
    }
  }
}
